import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationService } from 'projects/shared/services/notification.service';
import TruvBridge, { SuccessMetadata } from '@truv/bridge';
import { BorrowerVerificationService } from '../../services/borrower-verification.service';
import { EnrollmentEvent } from '../../models/borrower-verification-order-history.model';
import { firstValueFrom } from 'rxjs';
import { IncomeService } from '../../services/income-service';
import { ArgyleService } from '../../services/argyle.service';

@Component({
    selector: 'digital-income-employment-verification',
    templateUrl: './digital-income-employment-verification.component.html',
    styleUrls: ['./digital-income-employment-verification.component.scss'],
    standalone: false
})
export class DigitalIncomeEmploymentVerificationComponent implements OnInit, OnDestroy {

  @Input() applicationId: number;
  @Input() borrowerId: number;

  @Input() taskId: number | null | undefined;
  @Input() borrowerVerificationOrderId: number | null | undefined;

  @Input() provider: string = "Truv";
  @Input() thirdPartyEnrollmentWidget: string = "";

  /**
   * True when thirdPartyEnrollmentWidget is an order token. The widget then runs in order mode and the verification is
   * only complete once the order itself completes - one connected employer is just one part of the order.
   */
  @Input() usesOrdersApi: boolean = false;

  @Output() complete: EventEmitter<any> = new EventEmitter<any>();
  @Output() error: EventEmitter<any> = new EventEmitter<any>();
  @Output() close: EventEmitter<any> = new EventEmitter<any>();

  private _truvBridge: any;
  private _argyleLink: any;

  private _componentIsBeingDestroyed: boolean = false;

  private _linkCreated: boolean = false;
  private _verificationCompleted: boolean = false;

  /**
   * Latched for the duration of a completion attempt. Truv can report the same order as COMPLETED more than
   * once, and every `complete` emission advances the caller a step, so a duplicate would skip one.
   */
  private _completionLatched: boolean = false;

  constructor(
    private readonly _incomeService: IncomeService,
    private readonly _borrowerVerificationService: BorrowerVerificationService,
    private readonly _spinner: NgxSpinnerService,
    private readonly _notificationService: NotificationService,
    private readonly _argyleService: ArgyleService
  ) { }

  ngOnInit(): void {
    if (this.provider == "FormFree") {
      this.initializeFormFree();
    } else if (this.provider == "Truv") {
      this.initializeTruvBridge();
    } else if (this.provider == "Argyle") {
      this.initializeArgyleLink();
    }
  }

  ngOnDestroy(): void {
    this._componentIsBeingDestroyed = true;
    this._truvBridge?.close();
    this._argyleLink?.close();
  }

  private initializeFormFree = () => {
    let htmlWidgetSrc = this.thirdPartyEnrollmentWidget;
    htmlWidgetSrc = htmlWidgetSrc.replace(
      'element.parentNode.removeChild(element);',
      ''
    );

    document.getElementById("digital-income-employment-order-container").innerHTML = htmlWidgetSrc
    window.addEventListener('message', this.messageListener);
  }

  private initializeTruvBridge = () => {
    setTimeout(() => {
      this._truvBridge = TruvBridge.init({
        bridgeToken: this.thirdPartyEnrollmentWidget,
        isOrder: this.usesOrdersApi,
        position: {
          type: 'inline',
          container: document.querySelector('#digital-income-employment-order-container')
        },
        onLoad: () => {
          // Optional, called when Bridge loads
          console.log('Bridge loaded');
        },
        onSuccess: (public_token: string, metadata: SuccessMetadata) => {
          console.log('success handler')
          // Send the public_token to your server to exchange for an access_token
          // and retrieve payroll data.
          // The metadata object contains info about the Link.
          console.log("token: ", public_token)
          console.log("metadata: ", metadata)
        },
        onEvent: async (event_type: string, payload: any, source?: string) => {
          // all events fire this function. event_type indicates what the event is,
          // payload has additional information depending on the event. In order mode
          // Bridge passes the emitting surface ("order" page vs "bridge" widget) as a third argument.
          console.log('event: ', event_type)
          console.log('payload: ', payload)

          if (event_type == "LINK_CREATED") {
            // In order mode a created link is one employer of the order, not the end of the verification.
            this._linkCreated = true;
            await this.postEnrollmentEvent(event_type, payload);
          } else if (event_type == "COMPLETED" && this.usesOrdersApi && source == "order") {
            await this.postEnrollmentEvent(event_type, payload);
            await this.markVerificationAsComplete();
          } else if (event_type == "CLOSE" && !this.usesOrdersApi) {
            if (this._linkCreated) {
              await this.markVerificationAsComplete();
            }
          } else if (event_type == "ERROR") {
            await this.postEnrollmentEvent(event_type, payload);
            this.handleTruvError(payload);
          }
        },
        onClose: () => {
          // Optional, called when Bridge is closed by the user.
          console.log('Bridge closed');
          if (this._componentIsBeingDestroyed) {
            return;
          }

          // `complete` already advanced the caller; emitting `close` on the way out would advance it again.
          if (this._verificationCompleted) {
            return;
          }

          // Order mode reports its own completion, so closing before that just leaves the task open to resume.
          if (!this.usesOrdersApi && this._linkCreated) {
            return;
          }

          this.close.emit();
        }
      });

      this._truvBridge.open();
    }, 100);
  }

  private handleTruvError = (payload: any) => {
    const errorType = payload?.error?.error_type;
    console.error('Bridge error: ', payload);

    if (errorType == 'SESSION_EXPIRED') {
      this._notificationService.showError(
        "Your verification session expired. Please reopen the task to continue.",
        "Session Expired");
      return;
    }

    this._notificationService.showError(
      payload?.error?.error_message || "An error occurred during verification. Please try again.",
      "Verification Error");
  }

  private postEnrollmentEvent = async (eventType: string, payload: any) => {
    if (!this.borrowerVerificationOrderId) return;
    try {
      const event: EnrollmentEvent = {
        eventName: eventType,
        eventData: JSON.stringify(payload),
        eventDateTime: new Date().toISOString()
      };
      await firstValueFrom(this._borrowerVerificationService.postEnrollmentEvent(this.borrowerVerificationOrderId, event));
    } catch (error) {
      console.error('Failed to post enrollment event', error);
    }
  }

  private markVerificationAsComplete = async () => {
    if (this._completionLatched) {
      return;
    }
    this._completionLatched = true;

    try {
      if (this.taskId) {
        await firstValueFrom(this._borrowerVerificationService.markBorrowerVerificationTaskComplete(this.taskId));
      } else {
        await firstValueFrom(this._incomeService.markFormFreeForBorrowerComplete(this.borrowerId, this.applicationId));
      }
      this._verificationCompleted = true;
      this.complete.emit();
    } catch (error) {
      // A genuine failure should not block a later COMPLETED from retrying.
      this._completionLatched = false;
      this._notificationService.showError(error.message || "An error occurred while verifying income/employment digitally.", "Error!");
      this.error.emit();
    } finally {
      this._spinner.hide();
    }
  }

  private initializeArgyleLink = async () => {
    try {
      const Argyle = await this._argyleService.loadArgyleSdk();
      this._argyleLink = Argyle.create({
        userToken: this._argyleService.extractUserToken(this.thirdPartyEnrollmentWidget),
        sandbox: this._argyleService.isSandbox(),
        onAccountConnected: async ({ accountId, userId }: { accountId: string; userId: string }) => {
          console.log('Argyle account connected:', accountId);
          this._linkCreated = true;
          await this.postEnrollmentEvent('ACCOUNT_CONNECTED', { accountId, userId });
        },
        onAccountError: async ({ accountId, userId }: { accountId: string; userId: string }) => {
          console.log('Argyle account error:', accountId);
          await this.postEnrollmentEvent('ACCOUNT_ERROR', { accountId, userId });
        },
        onClose: async () => {
          console.log('Argyle Link closed');
          if (!this._componentIsBeingDestroyed) {
            if (this._linkCreated) {
              await this.markVerificationAsComplete();
            } else {
              this.close.emit();
            }
          }
        },
        onTokenExpired: async (updateToken: (token: string) => void) => {
          console.log('Argyle token expired, refreshing...');
          try {
            const response = await firstValueFrom(
              this._incomeService.initializeBorrowerIncomeAndEmploymentVerification(
                this.borrowerId, this.applicationId, 'Argyle'
              )
            );
            if (response?.htmlWidgetSrc) {
              updateToken(this._argyleService.extractUserToken(response.htmlWidgetSrc));
            }
          } catch (error) {
            console.error('Failed to refresh Argyle token', error);
          }
        },
        onError: ({ errorType, errorMessage }: { errorType: string; errorMessage: string }) => {
          console.error('Argyle Link error:', errorType, errorMessage);
          this._notificationService.showError(
            errorMessage || 'An error occurred during verification.',
            'Verification Error'
          );
        }
      });
      this._argyleLink.open();
    } catch (error) {
      console.error('Failed to initialize Argyle Link', error);
      this._notificationService.showError(
        'Failed to load verification widget. Please try again.',
        'Error'
      );
    }
  }

  private messageListener = async (msg: any) => {
    if (msg.data === 'closeFrame') {
      await this.markVerificationAsComplete();

      window.removeEventListener('message', this.messageListener);
    }
  };
}
